import classNames from 'classnames';
import React, { forwardRef, useState } from 'react';
import Icon from '@/components/atoms/icon/icon';
import Spinner from '@/components/atoms/spinner/spinner';
import Typography from '@/components/atoms/typography/typography';
import styles from './select.module.scss';

export interface SelectOption {
  disabled?: boolean;
  id?: string;
  name: string;
  selected?: boolean;
  value: string | number;
}

export interface SelectProps
  extends React.SelectHTMLAttributes<HTMLSelectElement> {
  /** Indicates whether the field is disabled */
  disabled?: boolean;
  /** A text that is shown below the field and affects the styling */
  error?: string;
  /** A text that is shown below the field as help text */
  help?: string;
  /** An icon to display next to the text */
  icon?: IconName;
  /** Indicates whether the field is loading or not */
  isLoading?: boolean;
  /** The text that is shown above the field as a label */
  label?: string;
  /** The text that is shown when the field is loading */
  loadingText?: string;
  /** An event that is triggered on blur */
  onBlur?: (event: React.FocusEvent<HTMLSelectElement>) => void;
  /** An event that is triggered on change */
  onChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  /** An event that is triggered on focus */
  onFocus?: (event: React.FocusEvent<HTMLSelectElement>) => void;
  /** An array of the value options */
  options?: SelectOption[];
  /** A text that is shown inside the field as a placeholder */
  placeholder?: string;
  /** The label to display when a value is selected (falls back to label if not provided) */
  selectedLabel?: string;
  /** The value of the field */
  value?: string;
}

const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      className,
      disabled,
      error,
      help,
      icon,
      isLoading,
      label,
      loadingText,
      options,
      placeholder,
      selectedLabel,
      ...rest
    },
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(false);

    const handleFocus = (e: React.FocusEvent<HTMLSelectElement>) => {
      setIsOpen(true);
      rest.onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLSelectElement>) => {
      setIsOpen(false);
      rest.onBlur?.(e);
    };

    const handleOnChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      setIsOpen(false);
      rest.onChange?.(e);
    };

    return (
      <div
        className={classNames(styles.component, className, {
          [styles.hasError]: error,
          [styles.hasIcon]: icon,
          [styles.hasLabel]: label,
          [styles.isDisabled]: disabled,
          [styles.isLoading]: isLoading,
        })}
      >
        <div className={styles.fieldWrapper}>
          {icon && <Icon className={styles.icon} name={icon} size={24} />}

          {label && (
            <Typography
              className={styles.label}
              htmlFor={rest.id}
              tag="label"
              tagStyle="bodySmallBold"
            >
              {rest.value && selectedLabel ? selectedLabel : label}

              {rest.required && <span className={styles.requiredMark}> *</span>}
            </Typography>
          )}

          <select
            {...rest}
            ref={ref}
            className={styles.field}
            disabled={disabled || isLoading}
            onBlur={handleBlur}
            onChange={handleOnChange}
            onFocus={handleFocus}
          >
            {placeholder && !isLoading && (
              <option disabled value="">
                {placeholder}
              </option>
            )}

            {isLoading && <option value="">{loadingText}</option>}

            {!isLoading &&
              options?.map(option => (
                <option
                  key={option.value}
                  disabled={option.disabled}
                  selected={option.selected}
                  value={option.value}
                >
                  {option.name}
                </option>
              ))}
          </select>

          {isLoading ? (
            <div className={styles.loadingIndicator}>
              <Spinner hasTransparentOverlay isSmall />
            </div>
          ) : (
            <Icon
              className={classNames(styles.chevronIcon, {
                [styles.isRotated]: isOpen,
              })}
              name="ChevronDown"
              size={20}
            />
          )}
        </div>

        {(error || help) && (
          <Typography className={styles.help} tagStyle="bodySmall">
            {error || help}
          </Typography>
        )}
      </div>
    );
  },
);

Select.displayName = 'Select';

export default Select;
